import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider as MuiThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import PrivateRoute from './components/PrivateRoute';

// Pages
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Companies from './pages/Companies';
import CompanyDetail from './pages/CompanyDetail';
import Kanban from './pages/Kanban';
import Calendar from './pages/Calendar';
import Finance from './pages/Finance';
import Users from './pages/Users';
import PendingApproval from './pages/PendingApproval';
import ChangeRequests from './pages/ChangeRequests';
import Archives from './pages/Archives';
import AccessRights from './pages/AccessRights';

// Dark theme with glassmorphism - Nord Aurora inspired
const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#88c0d0',
      light: '#a3d5e0',
      dark: '#5e9aad',
      contrastText: '#0d1117',
    },
    secondary: {
      main: '#b48ead',
      light: '#c9a8c4',
      dark: '#8f6d8a',
      contrastText: '#0d1117',
    },
    success: {
      main: '#a3be8c',
      light: '#bdd0a5',
      dark: '#7a9666',
    },
    warning: {
      main: '#ebcb8b',
      light: '#f0d9a8',
      dark: '#d4b56a',
    },
    error: {
      main: '#bf616a',
      light: '#d08088',
      dark: '#9a4a52',
    },
    info: {
      main: '#5e81ac',
      light: '#7e9bc0',
      dark: '#456385',
    },
    background: {
      default: '#0d1117',
      paper: 'rgba(22, 27, 34, 0.85)',
    },
    text: {
      primary: '#e6edf3',
      secondary: '#8b949e',
      disabled: '#6e7681',
    },
    divider: 'rgba(255, 255, 255, 0.08)',
    action: {
      hover: 'rgba(136, 192, 208, 0.08)',
      selected: 'rgba(136, 192, 208, 0.16)',
      focus: 'rgba(136, 192, 208, 0.12)',
    },
  },
  typography: {
    fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
    h1: { fontWeight: 800, letterSpacing: '-0.02em' },
    h2: { fontWeight: 700, letterSpacing: '-0.01em' },
    h3: { fontWeight: 700 },
    h4: { fontWeight: 700 },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 },
    subtitle1: { fontWeight: 500 },
    button: { fontWeight: 600, letterSpacing: '0.02em' },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundImage: `
            radial-gradient(ellipse at 20% 20%, rgba(136, 192, 208, 0.08) 0%, transparent 50%),
            radial-gradient(ellipse at 80% 80%, rgba(180, 142, 173, 0.08) 0%, transparent 50%),
            radial-gradient(ellipse at 40% 60%, rgba(94, 129, 172, 0.05) 0%, transparent 40%)
          `,
          backgroundAttachment: 'fixed',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 10,
          padding: '10px 20px',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        },
        contained: {
          background: 'linear-gradient(135deg, #88c0d0 0%, #5e81ac 100%)',
          boxShadow: '0 4px 20px rgba(136, 192, 208, 0.3)',
          '&:hover': {
            background: 'linear-gradient(135deg, #5e81ac 0%, #b48ead 100%)',
            boxShadow: '0 6px 30px rgba(136, 192, 208, 0.4)',
            transform: 'translateY(-2px)',
          },
        },
        outlined: {
          borderColor: 'rgba(136, 192, 208, 0.5)',
          color: '#88c0d0',
          backdropFilter: 'blur(10px)',
          '&:hover': {
            borderColor: '#88c0d0',
            background: 'rgba(136, 192, 208, 0.1)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          background: 'rgba(22, 27, 34, 0.7)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.06)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            border: '1px solid rgba(136, 192, 208, 0.2)',
            boxShadow: '0 12px 40px rgba(0, 0, 0, 0.5), 0 0 30px rgba(136, 192, 208, 0.1)',
            transform: 'translateY(-4px)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
        elevation1: {
          background: 'rgba(22, 27, 34, 0.7)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.06)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          background: 'rgba(13, 17, 23, 0.95)',
          backdropFilter: 'blur(30px) saturate(180%)',
          WebkitBackdropFilter: 'blur(30px) saturate(180%)',
          borderRight: '1px solid rgba(255, 255, 255, 0.06)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: 'rgba(13, 17, 23, 0.85)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
          boxShadow: '0 4px 30px rgba(0, 0, 0, 0.3)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            background: 'rgba(255, 255, 255, 0.02)',
            backdropFilter: 'blur(10px)',
            transition: 'all 0.3s ease',
            '& fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.1)',
              transition: 'all 0.3s ease',
            },
            '&:hover fieldset': { borderColor: 'rgba(136, 192, 208, 0.5)' },
            '&.Mui-focused fieldset': {
              borderColor: '#88c0d0',
              boxShadow: '0 0 20px rgba(136, 192, 208, 0.2)',
            },
          },
          '& .MuiInputLabel-root': {
            color: '#8b949e',
            '&.Mui-focused': { color: '#88c0d0' },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { backdropFilter: 'blur(10px)', transition: 'all 0.2s ease' },
        filled: {
          background: 'rgba(136, 192, 208, 0.2)',
          border: '1px solid rgba(136, 192, 208, 0.3)',
          '&:hover': { background: 'rgba(136, 192, 208, 0.3)' },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          background: 'rgba(22, 27, 34, 0.95)',
          backdropFilter: 'blur(30px) saturate(180%)',
          WebkitBackdropFilter: 'blur(30px) saturate(180%)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          boxShadow: '0 24px 80px rgba(0, 0, 0, 0.6)',
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          background: 'rgba(22, 27, 34, 0.95)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          boxShadow: '0 12px 40px rgba(0, 0, 0, 0.5)',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          transition: 'all 0.2s ease',
          '&:hover': { background: 'rgba(136, 192, 208, 0.1)' },
          '&.Mui-selected': {
            background: 'rgba(136, 192, 208, 0.15)',
            '&:hover': { background: 'rgba(136, 192, 208, 0.2)' },
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          transition: 'all 0.2s ease',
          '&:hover': { background: 'rgba(136, 192, 208, 0.08)' },
          '&.Mui-selected': {
            background: 'linear-gradient(135deg, rgba(136, 192, 208, 0.2) 0%, rgba(94, 129, 172, 0.2) 100%)',
            borderLeft: '3px solid #88c0d0',
            '&:hover': {
              background: 'linear-gradient(135deg, rgba(136, 192, 208, 0.25) 0%, rgba(94, 129, 172, 0.25) 100%)',
            },
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          transition: 'all 0.2s ease',
          '&.Mui-selected': { color: '#88c0d0' },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          background: 'linear-gradient(90deg, #88c0d0, #b48ead)',
          height: 3,
          borderRadius: 3,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          background: 'rgba(22, 27, 34, 0.95)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          fontSize: '0.875rem',
          padding: '8px 12px',
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(135deg, #88c0d0 0%, #5e81ac 100%)',
          fontWeight: 600,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: { backdropFilter: 'blur(10px)', border: '1px solid' },
        standardError: {
          background: 'rgba(191, 97, 106, 0.15)',
          borderColor: 'rgba(191, 97, 106, 0.3)',
        },
        standardSuccess: {
          background: 'rgba(163, 190, 140, 0.15)',
          borderColor: 'rgba(163, 190, 140, 0.3)',
        },
        standardWarning: {
          background: 'rgba(235, 203, 139, 0.15)',
          borderColor: 'rgba(235, 203, 139, 0.3)',
        },
        standardInfo: {
          background: 'rgba(136, 192, 208, 0.15)',
          borderColor: 'rgba(136, 192, 208, 0.3)',
        },
      },
    },
    MuiDivider: {
      styleOverrides: { root: { borderColor: 'rgba(255, 255, 255, 0.06)' } },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          transition: 'all 0.2s ease',
          '&:hover': {
            background: 'rgba(136, 192, 208, 0.1)',
            transform: 'scale(1.1)',
          },
        },
      },
    },
    MuiCircularProgress: {
      styleOverrides: { root: { color: '#88c0d0' } },
    },
  },
});

// Light theme
const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#5e81ac',
      light: '#81a1c1',
      dark: '#4c6a8f',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#b48ead',
      light: '#c9a8c4',
      dark: '#8f6d8a',
    },
    success: {
      main: '#a3be8c',
    },
    warning: {
      main: '#ebcb8b',
    },
    error: {
      main: '#bf616a',
    },
    background: {
      default: '#eceff4',
      paper: '#ffffff',
    },
    text: {
      primary: '#2e3440',
      secondary: '#4c566a',
    },
    divider: 'rgba(0, 0, 0, 0.08)',
  },
  typography: {
    fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
    h1: { fontWeight: 800, letterSpacing: '-0.02em' },
    h2: { fontWeight: 700, letterSpacing: '-0.01em' },
    h3: { fontWeight: 700 },
    h4: { fontWeight: 700 },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 },
    subtitle1: { fontWeight: 500 },
    button: { fontWeight: 600, letterSpacing: '0.02em' },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 10,
          padding: '10px 20px',
          transition: 'all 0.3s ease',
        },
        contained: {
          boxShadow: '0 4px 14px rgba(94, 129, 172, 0.25)',
          '&:hover': {
            boxShadow: '0 6px 20px rgba(94, 129, 172, 0.35)',
            transform: 'translateY(-2px)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          border: '1px solid rgba(0, 0, 0, 0.05)',
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)',
            transform: 'translateY(-4px)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          background: '#ffffff',
          borderRight: '1px solid rgba(0, 0, 0, 0.08)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: '#ffffff',
          color: '#2e3440',
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
          boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            '& fieldset': { borderColor: 'rgba(0, 0, 0, 0.15)' },
            '&:hover fieldset': { borderColor: '#5e81ac' },
            '&.Mui-focused fieldset': { borderColor: '#5e81ac' },
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          '&.Mui-selected': {
            background: 'linear-gradient(135deg, #5e81ac 0%, #81a1c1 100%)',
            color: '#ffffff',
            '& .MuiListItemIcon-root': { color: '#ffffff' },
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          background: 'linear-gradient(90deg, #5e81ac, #b48ead)',
          height: 3,
          borderRadius: 3,
        },
      },
    },
  },
});

const ThemedApp = () => {
  const { isDarkMode } = useTheme();
  const theme = isDarkMode ? darkTheme : lightTheme;

  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/pending" element={<PendingApproval />} />
            
            <Route path="/" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
            <Route path="/companies" element={<PrivateRoute><Companies /></PrivateRoute>} />
            <Route path="/companies/:id" element={<PrivateRoute><CompanyDetail /></PrivateRoute>} />
            <Route path="/kanban/:boardName" element={<PrivateRoute><Kanban /></PrivateRoute>} />
            <Route path="/calendar" element={<PrivateRoute><Calendar /></PrivateRoute>} />
            <Route path="/archives" element={<PrivateRoute><Archives /></PrivateRoute>} />
            <Route path="/finance" element={<PrivateRoute><Finance /></PrivateRoute>} />
            <Route path="/users" element={<PrivateRoute><Users /></PrivateRoute>} />
            <Route path="/change-requests" element={<PrivateRoute><ChangeRequests /></PrivateRoute>} />
            <Route path="/access-rights" element={<PrivateRoute><AccessRights /></PrivateRoute>} />
            
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
      </AuthProvider>
    </MuiThemeProvider>
  );
};

function App() {
  return (
    <ThemeProvider>
      <ThemedApp />
    </ThemeProvider>
  );
}

export default App;
